import { useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import '@/styles/lists.css'

/**
 * Панель страниц под таблицей: «← Назад · Строки 101–150 из 1 509 · Вперёд →».
 *
 * Номеров страниц нет намеренно. Список листается курсором (keyset, см. CLAUDE.md про
 * OFFSET): «перейти на страницу 900» означало бы OFFSET, то есть страницу в девятьсот раз
 * дороже первой. «Назад» и «Вперёд» стоят одинаково на любой глубине — назад ведёт
 * запомненный курсор предыдущей страницы, а не пересчёт.
 *
 * Общее число — если оно известно. Его считают отдельно и один раз на фильтр, поэтому
 * оно может прийти позже страницы или не прийти вовсе; диапазон строк от него не зависит.
 */
export const PAGE_SIZES = [25, 50, 100] as const

export function Pager({
  from,
  to,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  pageSize,
  onPageSize,
  busy,
}: {
  /** Номер первой строки страницы, с единицы; 0 — строк нет. */
  from: number
  to: number
  total?: number | null
  hasPrev: boolean
  hasNext: boolean
  onPrev: () => void
  onNext: () => void
  pageSize: number
  onPageSize?: (size: number) => void
  busy?: boolean
}) {
  const { t, locale } = useApp()
  const number = (value: number) => value.toLocaleString(locale)

  // Всё уместилось в самую короткую страницу — панель не нужна: две неактивные кнопки
  // и выбор размера под таблицей из десяти строк только занимают место.
  const fitsSmallestPage = total !== undefined && total !== null && total <= PAGE_SIZES[0]
  if (!hasPrev && !hasNext && (fitsSmallestPage || (!onPageSize && from === 0))) {
    return null
  }

  return (
    <nav className="pager" aria-label={t('pager.label')}>
      <button
        className={`btn btn-sm ${hasPrev ? 'btn-secondary' : 'btn-ghost'}`}
        type="button"
        onClick={onPrev}
        disabled={!hasPrev || busy}
      >
        <Icon name="arrow-left" size="sm" />
        {t('pager.prev')}
      </button>
      <span aria-live="polite">
        {from === 0 ? (
          t('pager.empty')
        ) : (
          <span className="range">{t('pager.range', { from: number(from), to: number(to) })}</span>
        )}
        {total !== undefined && total !== null && from !== 0 && (
          <> {t('pager.of', { total: number(total) })}</>
        )}
      </span>
      <button
        className="btn btn-secondary btn-sm"
        type="button"
        onClick={onNext}
        disabled={!hasNext || busy}
        aria-busy={busy || undefined}
      >
        {busy && hasNext ? (
          <>
            <span className="spinner pager-spinner" aria-hidden="true" />
            {t('pager.loading')}
          </>
        ) : (
          <>
            {t('pager.next')}
            <Icon name="arrow-right" size="sm" />
          </>
        )}
      </button>
      {!hasPrev && from !== 0 && <span className="muted t-xs">{t('pager.start')}</span>}
      {onPageSize && (
        <>
          <span className="spacer" />
          <span className="muted">{t('pager.size')}</span>
          {/* Сегменты, а не выпадающий список (дизайн: «25 / 50 / 100»): вариантов три,
              и текущий виден без раскрытия. */}
          <div className="seg" role="group" aria-label={t('pager.size')}>
            {PAGE_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                aria-pressed={pageSize === size}
                onClick={() => onPageSize(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </>
      )}
    </nav>
  )
}

/**
 * Постраничный показ строк, уже целиком лежащих на клиенте (сводные отчёты).
 *
 * Отчёт приходит одним ответом — это агрегат, а не лента записей, — но 476 строк одной
 * таблицей высотой в тринадцать тысяч пикселей не читаются. Страница сбрасывается на
 * первую, когда меняется сам набор строк: иначе новый отчёт открывался бы с середины.
 */
export function useClientPages<T>(rows: readonly T[], initialSize = 50) {
  const [pageSize, setPageSize] = useState(initialSize)
  const [page, setPage] = useState(0)
  // Сброс по числу строк, а не по ссылке на массив: ссылка меняется у вызывающего от
  // любой отрисовки (`data ?? []`), и «Вперёд» тут же откатывался бы на первую страницу.
  useEffect(() => setPage(0), [rows.length, pageSize])

  // Страница за концом набора (строк стало меньше раньше, чем сработал сброс) —
  // показывается последняя, а не пустая таблица.
  const lastPage = Math.max(0, Math.ceil(rows.length / pageSize) - 1)
  const start = Math.min(page, lastPage) * pageSize
  const visible = rows.slice(start, start + pageSize)
  return {
    visible,
    pager: {
      from: rows.length === 0 ? 0 : start + 1,
      to: start + visible.length,
      total: rows.length,
      hasPrev: start > 0,
      hasNext: start + pageSize < rows.length,
      onPrev: () => setPage((current) => Math.max(0, Math.min(current, lastPage) - 1)),
      onNext: () => setPage((current) => current + 1),
      pageSize,
      onPageSize: setPageSize,
    },
  }
}
